import { classNames } from "helpers";
import React from "react";
import { ReactSVG } from "react-svg";
import { ButtonProps } from "./types";

const Button: React.FC<ButtonProps> = ({
  onClick,
  children,
  disabled = false,
  size,
  startIcon,
  endIcon,
  variant,
  className,
  type = "button",
  loading
}) => {
  const sizeClassName = () => {
    switch (size) {
      case "small":
        return "px-3 py-2 text-sm";
      case "medium":
        return "px-3 py-2";
      case "large":
        return "px-3.5 py-3.5";
      case "full":
        return "w-full px-3 py-3 justify-center";
      default:
        return "w-max px-3 py-3";
    }
  };

  const variantClassName = () => {
    switch (variant) {
      case "text":
        return "text-blue-500";
      case "outlined":
        // Ring ostaje i na hover, samo mijenja boju — gašenjem bi gumb izgubio 1 px
        // obruba i djelovao manji od susjednog polja.
        //
        // `ring-inset` jer se ring inače crta izvan granice elementa: bez njega je outlined
        // gumb 2 px širi i viši od primarnog, što se vidi tek kad stoje jedan uz drugi.
        return "ring-1 ring-inset ring-slate-300 hover:bg-blue-600 hover:ring-blue-600 hover:text-white transition ease-in-out delay-150";
      case "primary":
        return "bg-blue-500 text-white hover:bg-blue-600 hover:text-white transition ease-in-out delay-150";
      case "secondary":
        return "bg-blue-100 text-slate-600 hover:bg-blue-600 hover:text-white transition ease-in-out delay-150";
      case "remove":
        return "bg-red-100 text-red-600 hover:bg-red-500 hover:text-white transition ease-in-out delay-150";
      case "warning":
        return "bg-amber-100 text-amber-700 hover:bg-amber-500 hover:text-white transition ease-in-out delay-150";
      case "success":
        return "bg-green-100 text-green-700 hover:bg-green-600 hover:text-white transition ease-in-out delay-150";
      default:
        return "";
    }
  };

  const iconClassName = () => {
    switch (variant) {
      case "text":
        return "stroke-[#181b31]";
      case "outlined":
        return "stroke-[#181b31] group-hover:stroke-white";
      case "primary":
        return "stroke-[#fff] group-hover:stroke-white";
      case "secondary":
        return "stroke-slate-600 group-hover:stroke-white";
      case "remove":
        return "stroke-red-600 group-hover:stroke-white";
      case "warning":
        return "stroke-amber-700 group-hover:stroke-white";
      case "success":
        return "stroke-green-700 group-hover:stroke-white";
      default:
        return "";
    }
  };

  const iconSizeClassName = () => {
    switch (size) {
      case "small":
        return "w-[14px] h-[14px]";
      case "medium":
        return "w-[16px] h-[16px]";
      case "large":
        return "w-[20px] h-[20px]";
      default:
        return "w-[18px] h-[18px]";
    }
  };

  return (
    <>
      <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={classNames(
          "group",
          className,
          sizeClassName(),
          variantClassName(),
          "group flex items-center text-sm font-medium rounded-sm "
        )}

      >
        {startIcon && (
          <ReactSVG
            src={startIcon}
            className={classNames(
              iconClassName(),
              iconSizeClassName(),
              "transition ease-in-out delay-150"
            )}
          />
        )}
        <span>
          {children ? (
            <span className="ml-1 mr-1 inline-block">{children}</span>
          ) : (
            <span className="h-5 block"></span>
          )}
        </span>
        {endIcon && (
          <ReactSVG
            src={endIcon}
            className={classNames(
              iconClassName(),
              iconSizeClassName(),
              "transition ease-in-out delay-150"
            )}
          />
        )}
      </button>
    </>
  );
};

export default React.memo(Button);
